// Lazy Pyodide loader. Assets are self-hosted under /pyodide-0.27.5/ (bundled
// into the SPA at build time from frontend/public/pyodide-0.27.5/) so the
// candidate flow makes zero cross-origin requests and stays inside the Fly
// Frankfurt envelope. Subsequent calls reuse the same instance.

declare global {
  interface Window {
    loadPyodide?: (opts?: { indexURL?: string }) => Promise<PyodideRuntime>;
  }
}

export interface PyodideRuntime {
  runPython: (code: string) => unknown;
  runPythonAsync: (code: string) => Promise<unknown>;
  globals: {
    set: (name: string, value: unknown) => void;
    get: (name: string) => unknown;
  };
}

// Pyodide is bundled under frontend/public/pyodide-0.27.5/ (refresh from a new
// pyodide-core-*.tar.bz2 release when bumping). Immutable-cached by URL: when
// you refresh these vendored bytes, rename the dir AND bump the version below,
// or stale bytes are served forever (see backend SPAStaticFiles cache policy).
const PYODIDE_BASE = "/pyodide-0.27.5/";
// Exported so pyodide.test.ts imports the real path instead of duplicating the
// literal — a duplicate drifted once (the P4 vendor-dir bump updated this file
// but not the test's hardcoded "/pyodide/pyodide.js", breaking the suite).
export const PYODIDE_SCRIPT = `${PYODIDE_BASE}pyodide.js`;
// CP-17: the pre-test connection check downloads this file (2.36 MB, already
// zip-compressed, immutable-cached) as its same-origin throughput probe. Kept
// here, next to PYODIDE_SCRIPT, so a vendor-dir bump cannot leave the probe
// pointing at a path the SPA fallback answers with index.html.
export const PYODIDE_STDLIB_ZIP = `${PYODIDE_BASE}python_stdlib.zip`;

let runtimePromise: Promise<PyodideRuntime> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => {
      // Drop the failed tag so it can't short-circuit a later retry's
      // querySelector check and re-fetch never happens (see getPyodide below).
      s.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(s);
  });
}

export function getPyodide(): Promise<PyodideRuntime> {
  if (runtimePromise) return runtimePromise;
  const attempt = (async () => {
    await loadScript(PYODIDE_SCRIPT);
    if (!window.loadPyodide) {
      // onload fired but the global never appeared (partial/corrupt exec). The
      // tag is still in the DOM and would make loadScript short-circuit on every
      // retry WITHOUT re-fetching — remove it so the reset below can actually heal.
      document.querySelector(`script[src="${PYODIDE_SCRIPT}"]`)?.remove();
      throw new Error("Pyodide global not available after script load");
    }
    return window.loadPyodide({ indexURL: PYODIDE_BASE });
  })();
  runtimePromise = attempt;
  // A rejected load must not be cached forever: clear the singleton so the next
  // getPyodide() call retries from scratch (a flaky Gate idle-preload or a
  // dropped mid-download self-heals). Identity guard: only clear if a later
  // successful attempt hasn't already replaced it. On success the singleton
  // stays put → one runtime per page, never double-loaded.
  attempt.catch(() => {
    if (runtimePromise === attempt) runtimePromise = null;
  });
  return attempt;
}

export interface RunResult {
  passed: boolean;
  output: string;
  error?: string;
  /**
   * Set when `ast.literal_eval(testInput)` itself failed — i.e. the TEST INPUT
   * isn't a Python literal, as opposed to a bug in the code under test. Decided
   * in-harness (a dedicated try around the parse) rather than by sniffing the
   * traceback, so a solution that itself calls `ast.literal_eval` can't be
   * mistaken for a bad input.
   */
  input_error?: boolean;
}

export function detectFunctionName(
  starterCode: string | null | undefined,
  userCode: string,
): string {
  // Anchor on the function the starter advertises so a user-defined helper at
  // the top of the file isn't called by mistake.
  const starterMatch = starterCode?.match(
    /def\s+([A-Za-z_][A-Za-z0-9_]*)\s*\(/,
  );
  const userMatch = userCode.match(/def\s+([A-Za-z_][A-Za-z0-9_]*)\s*\(/);
  return starterMatch?.[1] ?? userMatch?.[1] ?? "solution";
}

export async function runUserCodeAgainstTest(
  userCode: string,
  testInput: string,
  expectedOutput: string,
  functionName: string
): Promise<RunResult> {
  const py = await getPyodide();
  const harness = `
import ast, json, traceback, io, contextlib
_ns = {}
try:
    exec(${JSON.stringify(userCode)}, _ns)
    fn = _ns.get(${JSON.stringify(functionName)})
    if fn is None:
        _result = {"passed": False, "output": "", "error": "Function ${functionName} not defined"}
    else:
        # Parse the test INPUT in its own try so a literal_eval failure is
        # unambiguously the INPUT's fault (flagged input_error) — never confused
        # with a bug in a solution that itself calls ast.literal_eval, which a
        # traceback-sniffing caller would mis-label. Use literal_eval (not eval)
        # and splat multi-arg tuples, mirroring the authoritative server grader
        # (backend/grading/pyodide_grader.js _run_one) so this returns identical
        # pass/fail. An input like "[1,2,3], 3" parses to a tuple; splat it for
        # >1-param functions (the natural def f(a, b) form), while single-param
        # functions — incl. the LeetCode list form — keep receiving it whole.
        try:
            _arg = ast.literal_eval(${JSON.stringify(testInput)})
        except Exception:
            _result = {"passed": False, "output": "", "error": traceback.format_exc(), "input_error": True}
        else:
            _co = getattr(fn, "__code__", None)
            if isinstance(_arg, tuple) and _co is not None and _co.co_argcount > 1 and not (_co.co_flags & 0x04):
                _out = fn(*_arg)
            else:
                _out = fn(_arg)
            _ok = repr(_out) == ${JSON.stringify(expectedOutput)} or str(_out) == ${JSON.stringify(expectedOutput)}
            _result = {"passed": bool(_ok), "output": repr(_out)}
except Exception:
    _result = {"passed": False, "output": "", "error": traceback.format_exc()}
json.dumps(_result)
`.trim();
  const raw = (await py.runPythonAsync(harness)) as string;
  return JSON.parse(raw) as RunResult;
}
